import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import PublicLayout from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar } from '../../components/ui/calendar';
import { 
  ArrowLeft, 
  MessageCircle, 
  Clock, 
  DollarSign,
  CheckCircle,
  XCircle,
  Droplets,
  Sun,
  Gamepad2,
  Users,
  ChevronLeft,
  ChevronRight,
  Baby
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WHATSAPP_NUMBER = '529993607922';

const categoriaIcons = {
  agua: Droplets,
  seco: Sun,
  interactivo: Gamepad2,
};

const categoriaLabels = {
  agua: 'Acuático',
  seco: 'Seco',
  interactivo: 'Interactivo',
};

export default function InflableDetailPage() {
  const { id } = useParams();
  const [inflable, setInflable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchInflable = async () => {
      try {
        const response = await axios.get(`${API_URL}/inflables/${id}`);
        setInflable(response.data);
      } catch (error) {
        console.error('Error fetching inflable:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInflable();
  }, [id]);

  useEffect(() => {
    const checkAvailability = async () => {
      if (!selectedDate || !id) return;
      
      setCheckingAvailability(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await axios.get(`${API_URL}/disponibilidad/${id}/${dateStr}`);
        setAvailability(response.data);
      } catch (error) {
        console.error('Error checking availability:', error);
      } finally {
        setCheckingAvailability(false);
      }
    };
    checkAvailability();
  }, [selectedDate, id]);

  const getWhatsAppMessage = () => {
    let message = `Hola! Me interesa rentar el inflable "${inflable?.nombre}"`;
    if (selectedDate) {
      message += ` para el dia ${format(selectedDate, "d 'de' MMMM 'de' yyyy", { locale: es })}`;
    }
    message += `. Esta disponible?`;
    return encodeURIComponent(message);
  };

  const images = inflable?.imagenes?.length > 0 
    ? inflable.imagenes 
    : inflable?.imagen_url 
      ? [inflable.imagen_url] 
      : [];

  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </PublicLayout>
    );
  }

  if (!inflable) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <p className="text-allegra-gray text-lg mb-4">Inflable no encontrado</p>
          <Link to="/catalogo">
            <Button variant="outline" className="rounded-full gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver al catalogo
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const CategoryIcon = categoriaIcons[inflable.categoria] || Sun;

  return (
    <PublicLayout>
      {/* Breadcrumb */}
      <div className="bg-allegra-light py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/catalogo" className="inline-flex items-center gap-2 text-allegra-gray hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al catalogo
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Gallery with Slider */}
            <div className="space-y-4">
              <div className="relative rounded-3xl overflow-hidden shadow-xl">
                {images.length > 0 ? (
                  <img 
                    src={images[currentImageIndex]} 
                    alt={inflable.nombre}
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                    data-testid="inflable-main-image"
                  />
                ) : (
                  <div className="w-full h-[400px] lg:h-[500px] bg-muted flex items-center justify-center">
                    <p className="text-allegra-gray">Sin imagen</p>
                  </div>
                )}
                <Badge className="absolute top-4 left-4 bg-white/90 text-allegra-dark gap-1 text-sm">
                  <CategoryIcon className="w-4 h-4" />
                  {categoriaLabels[inflable.categoria]}
                </Badge>
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-5 h-5 text-allegra-dark" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-5 h-5 text-allegra-dark" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${
                            idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/60'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`rounded-xl overflow-hidden border-2 transition-all ${
                        index === currentImageIndex ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`${inflable.nombre} ${index + 1}`}
                        className="w-full h-20 object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-allegra-dark mb-4" data-testid="inflable-name">
                {inflable.nombre}
              </h1>
              <p className="text-allegra-gray text-lg mb-6" data-testid="inflable-description">
                {inflable.descripcion}
              </p>

              {/* Price Card */}
              <Card className="border-0 shadow-lg mb-6 bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <span className="text-sm text-allegra-gray">Precio base (6 hrs)</span>
                      <p className="font-heading font-bold text-3xl text-primary" data-testid="inflable-price">
                        ${inflable.precio_base}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-allegra-gray">Hora extra</span>
                      <p className="font-heading font-bold text-2xl text-allegra-dark">
                        +${inflable.precio_hora_extra}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-allegra-gray">
                      <DollarSign className="w-5 h-5 text-accent" />
                      <span>Anticipo requerido: <strong className="text-allegra-dark">${inflable.anticipo_requerido}</strong></span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                  <Clock className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="font-medium text-allegra-dark">6 horas</p>
                    <p className="text-sm text-allegra-gray">Tiempo incluido</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                  <Users className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="font-medium text-allegra-dark" data-testid="inflable-capacidad">
                      {inflable.capacidad_ninos ? `${inflable.capacidad_ninos} niños` : '10+ niños'}
                    </p>
                    <p className="text-sm text-allegra-gray">Capacidad</p>
                  </div>
                </div>
                {inflable.rango_edades && (
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-xl col-span-2">
                    <Baby className="w-5 h-5 text-secondary" />
                    <div>
                      <p className="font-medium text-allegra-dark" data-testid="inflable-edades">
                        {inflable.rango_edades}
                      </p>
                      <p className="text-sm text-allegra-gray">Rango de edades</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Calendar */}
              <Card className="border-0 shadow-lg mb-6">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg text-allegra-dark mb-4">
                    Verificar Disponibilidad
                  </h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="rounded-xl border"
                    data-testid="availability-calendar"
                  />
                  
                  {selectedDate && (
                    <div className="mt-4 p-4 rounded-xl bg-muted">
                      <p className="text-sm text-allegra-gray mb-2">
                        Fecha seleccionada: <strong>{format(selectedDate, "d 'de' MMMM 'de' yyyy", { locale: es })}</strong>
                      </p>
                      {checkingAvailability ? (
                        <div className="flex items-center gap-2 text-allegra-gray">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                          Verificando...
                        </div>
                      ) : availability ? (
                        <div className={`flex items-center gap-2 ${availability.disponible ? 'text-green-600' : 'text-red-600'}`} data-testid="availability-status">
                          {availability.disponible ? (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">Disponible!</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-5 h-5" />
                              <span className="font-medium">No disponible en esta fecha</span>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* CTA */}
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${getWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="w-full rounded-full gap-2 text-lg shadow-lg shadow-primary/25" data-testid="cotizar-whatsapp-btn">
                  <MessageCircle className="w-5 h-5" />
                  Solicitar Cotizacion por WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
